import {
  FULFILLMENT_IGNORE_RULE_MATCHES,
  FULFILLMENT_IGNORE_RULE_MATCH_LABELS,
  fulfillmentIgnoreRuleInputSchema,
  type FulfillmentIgnoreRule,
  type FulfillmentIgnoreRuleInput,
  type FulfillmentIgnoreRuleMatch,
} from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { createIgnoreRule, deleteIgnoreRule, fetchIgnoreRules, fulfillmentQueryKeys } from './api-martelet.js';
import { SectionTable, SettingsSectionSkeleton } from './settings-parts.js';

const SECTION_TITLE = 'Commandes à ignorer au Suivi Martelet';
const SECTION_DESCRIPTION =
  'Les mises à jour Martelet dont le numéro de commande correspond sont ignorées sans alerte — par exemple les commandes créées directement chez Martelet (UGC, Toasty). Une nouvelle règle s’applique aussi aux mises à jour déjà en erreur.';

const EMPTY_INPUT: FulfillmentIgnoreRuleInput = { match: 'startsWith', value: '', reason: '' };

/**
 * Règles « commandes à ignorer » (`01` §5.3.0) : écartent sans alerte les
 * retours Martelet de commandes qui n'existent pas dans Shopify.
 */
export function IgnoreRulesSection(): JSX.Element {
  const queryClient = useQueryClient();
  const [input, setInput] = useState<FulfillmentIgnoreRuleInput>(EMPTY_INPUT);
  const deleteDialog = useConfirmDialog<FulfillmentIgnoreRule>();

  const query = useQuery({ queryKey: fulfillmentQueryKeys.ignoreRules(), queryFn: fetchIgnoreRules });

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: fulfillmentQueryKeys.ignoreRules() });
    void queryClient.invalidateQueries({ queryKey: queryKeys.runs.all });
  }

  const createMutation = useMutation({
    mutationFn: createIgnoreRule,
    onSuccess: (result) => {
      toast.success(
        result.appliedToExisting > 0
          ? `Règle ajoutée : ${String(result.appliedToExisting)} mise(s) à jour en erreur ignorée(s).`
          : 'Règle ajoutée.',
      );
      setInput(EMPTY_INPUT);
      invalidate();
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteIgnoreRule(id),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Règle supprimée.');
      invalidate();
      deleteDialog.close();
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    const parsed = fulfillmentIgnoreRuleInputSchema.safeParse(input);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Règle invalide.');
      return;
    }
    createMutation.mutate(parsed.data);
  }

  if (query.isPending) {
    return <SettingsSectionSkeleton fields={3} />;
  }
  if (query.isError) {
    return (
      <PageSection title={SECTION_TITLE}>
        <ErrorState
          message={query.error instanceof ApiError ? query.error.message : 'Impossible de charger les règles.'}
          detail={query.error instanceof ApiError ? query.error.detail : undefined}
          onRetry={() => {
            void query.refetch();
          }}
          className="border-0 bg-transparent py-6"
        />
      </PageSection>
    );
  }

  const rules = query.data;

  return (
    <>
      <PageSection title={SECTION_TITLE} description={SECTION_DESCRIPTION}>
        <div className="flex flex-col gap-5">
          <form className="flex flex-wrap items-end gap-2" onSubmit={submit}>
            <Field label="Le numéro de commande" htmlFor="ignore-rule-match" className="w-full sm:w-44">
              <Select
                value={input.match}
                onValueChange={(value) => {
                  setInput((previous) => ({ ...previous, match: value as FulfillmentIgnoreRuleMatch }));
                }}
              >
                <SelectTrigger id="ignore-rule-match" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FULFILLMENT_IGNORE_RULE_MATCHES.map((match) => (
                    <SelectItem key={match} value={match}>
                      {FULFILLMENT_IGNORE_RULE_MATCH_LABELS[match]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Valeur" htmlFor="ignore-rule-value" className="w-full sm:w-40">
              <Input
                id="ignore-rule-value"
                placeholder="UGC_"
                value={input.value}
                onChange={(event) => {
                  setInput((previous) => ({ ...previous, value: event.target.value }));
                }}
              />
            </Field>
            <Field label="Motif" htmlFor="ignore-rule-reason" className="min-w-0 flex-1 sm:min-w-64">
              <Input
                id="ignore-rule-reason"
                placeholder="Envois d’influence créés chez Martelet"
                value={input.reason}
                onChange={(event) => {
                  setInput((previous) => ({ ...previous, reason: event.target.value }));
                }}
              />
            </Field>
            <Button type="submit" isLoading={createMutation.isPending}>
              <Plus aria-hidden="true" />
              Ajouter
            </Button>
          </form>

          {rules.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune règle : toutes les mises à jour Martelet sont traitées.</p>
          ) : (
            <SectionTable atEnd>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Le numéro de commande</TableHead>
                    <TableHead>Motif</TableHead>
                    <TableHead className="text-right">Ignorées</TableHead>
                    <TableHead className="text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell className="whitespace-nowrap">
                        {FULFILLMENT_IGNORE_RULE_MATCH_LABELS[rule.match]}{' '}
                        <span className="font-mono text-[13px]">{rule.value}</span>
                      </TableCell>
                      <TableCell>{rule.reason}</TableCell>
                      <TableCell className="text-right tabular-nums">{rule.ignoredCount}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Supprimer la règle ${rule.value}`}
                          onClick={() => {
                            deleteDialog.open(rule);
                          }}
                        >
                          <Trash2 aria-hidden="true" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SectionTable>
          )}
        </div>
      </PageSection>

      <ConfirmDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            deleteDialog.close();
          }
        }}
        title="Supprimer cette règle"
        description={
          deleteDialog.target
            ? `Les prochaines mises à jour dont le numéro ${FULFILLMENT_IGNORE_RULE_MATCH_LABELS[deleteDialog.target.match]} « ${deleteDialog.target.value} » seront de nouveau traitées. Celles déjà ignorées le restent.`
            : ''
        }
        confirmLabel="Supprimer"
        isConfirming={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteDialog.target) {
            deleteMutation.mutate(deleteDialog.target.id);
          }
        }}
      />
    </>
  );
}
